import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { flatten, pick, prop } from 'ramda'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import { GridViewColumn } from 'core/elements/grid/Grid'
import {
  listResmgrHosts,
  listHypervisors,
  getHostNetworkConfigs,
  listHostNetworkConfigs,
  getBlueprints,
} from '../actions'
import { listStoragePools } from 'openstack/components/storage/pools/actions'
import { storagePoolsSelector } from 'openstack/components/storage/pools/selectors'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import {
  resmgrHostsSelector,
  hypervisorsSelector,
  UNASSIGNED_CLUSTER,
  hostAggregateOptionsSelector,
  healthyStatuses,
  pendingStatuses,
  errorStatuses,
  unknownStatuses,
} from '../selectors'
import InferActionParams from 'core/actions/InferActionParams'
import Text from 'core/elements/Text'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { routeManager } from 'core/utils/routes'
import { formatDate } from 'utils/misc'
import { IHostDetailsPageTabs } from './host-details/model'
import { useSelector } from 'react-redux'
import HostsListPageHeader from './HostsListPageHeader'
import ApiClient from 'api-client/ApiClient'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import {
  GridBatchActionSpec,
  GridDropdownBatchActionGroup,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import EditServerRolesModal from './EditServerRolesModal'
import ToggleMaintenanceModeDialog from './ToggleMaintenanceModeDialog'
import EnableMaintenanceModeDialog from './EnableMaintenanceModeDialog'
import DisableMaintenanceDialog from './host-details/DisableMaintenanceDialog'
import AuthorizeVmwareGatewayModal from './AuthorizeVmwareGatewayModal'
import DeauthorizeRolesDialog from './DeauthorizeRolesDialog'
import DeauthorizeHostDialog from './DeauthorizeHostDialog'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import PollingData from 'core/components/PollingData'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import AllocationRatioModal from './AllocationRatioModal'
import ExternalLink from 'core/components/ExternalLink'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'
import useReactRouter from 'use-react-router'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import RadioDropdownFilter, {
  Item,
} from 'openstack/components/networks/dns/records/RadioDropdownFilter'
import { listClusterHostGroups } from '../clusters/actions'
import { clusterHostGroupsSelector } from '../clusters/selectors'
import { IClusterHostGroup } from '../clusters/model'
import AddServersPage from './AddServersPage'
import DeauthorizeHostModal from './DeauthorizeHostModal'
import AuthorizeVmwareGatewayModalCopy from 'app/plugins/opencloud/components/AuthorizeVmwareGatewayModalCopy'
import StringMultiDropdownFilter from 'core/elements/grid/filters/StringMultiDropdownFilter'
import { isOpenCloud, isReadonlyUser } from 'core/utils/helpers'
import Tab from 'core/elements/tabs/Tab'
import Tabs from 'core/elements/tabs/Tabs'
import CreateButton from 'core/components/buttons/CreateButton'
import { getProductName } from 'core/utils/helpers'
import clsx from 'clsx'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ExpandableHostAggregateMetadataView from './ExpandableHostAggregateMetadataView'
import ResourceUsageIndicator from 'app/plugins/infrastructure/components/common/ResourceUsageIndicator'
import { toGiB, toMHz } from '../helpers'
import Badge from 'core/elements/badge'
import Tooltip from 'core/elements/tooltip'

export enum IHostsListPageTabs {
  HostsList = 'list',
  ResourceUtilization = 'resource-utilization',
}

type ModelDataKey = DataKeys.ResmgrHosts
type SelectorModel = ArrayElement<ReturnType<typeof resmgrHostsSelector>>
type ActionParams = InferActionParams<typeof listResmgrHosts>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ResmgrHosts',
  listTablePrefs,
)

const searchTargets = [
  'name',
  'id',
  'interfaces',
  'clusterName',
  'roleStatuses',
  'responding',
  'schedulingStatus',
]

const getSearchFunctions = (isVmware) => [
  {
    property: 'roleStatuses',
    searchFn: (value, searchString) => {
      return value.some((role) => {
        return (
          role?.display?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          role?.status?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
  {
    property: 'schedulingStatus',
    searchFn: (value, searchString, item) => {
      const underMaintenance = !!item?.hypervisor?.service?.disabled_reason
      return (
        value?.toLowerCase?.().includes(searchString.toLowerCase()) ||
        (underMaintenance && 'Maintenance Mode'.toLowerCase().includes(searchString.toLowerCase()))
      )
    },
  },
  {
    property: 'interfaces',
    searchFn: (value, searchString) => {
      return value.some((iface) => {
        return (
          iface?.name?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          iface?.ip?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
]

export const IpAddressCellComponent = ({ value }) => {
  const classes = useStyles({})
  return (
    <div>
      {value?.map((iface) => (
        <div key={iface.ip} className={classes.ipAddressRow}>
          <Text variant="body2" component="div">
            {iface.ip?.split('%')?.[0]}
          </Text>
          <Text variant="body2" component="div">
            {iface.name}
          </Text>
        </div>
      ))}
    </div>
  )
}

const RespondingCell = ({ value, item }) => {
  const classes = useStyles({ state: 'ok', responding: value === 'online' })
  return (
    <div>
      <Text variant="body2" className={classes.nowrap}>
        <FontAwesomeIcon className={classes.icon} solid>
          circle
        </FontAwesomeIcon>
        {value === 'offline' ? (
          <ExternalLink url="https://platform9.com/docs/private-cloud-director/troubleshooting/hypervisor-or-host-issues">
            {value}
          </ExternalLink>
        ) : (
          value
        )}
      </Text>
      {value === 'offline' && item?.lastResponseTime && (
        <Text variant="body2">(since {formatDate(`${item.lastResponseTime}Z`)})</Text>
      )}
    </div>
  )
}

export const RoleStatusesCell = ({ value, item }) => {
  const classes = useStyles({})
  const roles = item?.v1Roles
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  if (!roles?.length) {
    return <Text variant="body2">Unauthorized</Text>
  }
  return (
    <div>
      {value.map((role) => (
        <Text variant="body2" className={classes.nowrap} key={role.role}>
          <FontAwesomeIcon
            className={clsx({
              [classes.dot]: true,
              [classes.green]: healthyStatuses.includes(role.status),
              [classes.orange]: unknownStatuses.includes(role.status),
              [classes.yellow]: pendingStatuses.includes(role.status),
              [classes.red]: errorStatuses.includes(role.status),
            })}
            solid
          >
            circle
          </FontAwesomeIcon>
          {role.display} -{' '}
          {errorStatuses.includes(role.status) ? (
            <ExternalLink url="https://platform9.com/docs/private-cloud-director/troubleshooting/hypervisor-or-host-issues">
              {role.status}
            </ExternalLink>
          ) : (
            role.status
          )}
        </Text>
      ))}
      {isVmware && item?.vmwareRolesIncomplete && (
        <div className={classes.warning}>
          <FontAwesomeIcon className={classes.missingPackages}>
            triangle-exclamation
          </FontAwesomeIcon>
          <Text variant="body2">vSphere Gateway Authorization Incomplete</Text>
        </div>
      )}
    </div>
  )
}

const ResourceUsageIndicators = ({
  usageStats,
  allocationStats,
  usageValueConverter = null,
  allocationValueConverter = null,
}) => {
  return (
    <>
      <ResourceUsageIndicator
        valueConverter={usageValueConverter?.fn}
        units={usageValueConverter?.units || usageStats?.units}
        label="Used"
        stats={usageStats}
      />
      <ResourceUsageIndicator
        valueConverter={allocationValueConverter?.fn}
        units={allocationValueConverter?.units || allocationStats?.units}
        label="Allocated"
        stats={allocationStats}
      />
    </>
  )
}

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id }) =>
        routeManager.routes.openstack.hostDetails.path({ id, tab: IHostDetailsPageTabs.Overview }),
    }),
  },
  {
    key: 'id',
    label: 'ID',
    display: false,
  },
  {
    key: 'hostAggregateMetadata',
    label: 'Host Aggregates',
    CellComponent: ({ value, item }) => (
      <ExpandableHostAggregateMetadataView itemName={item?.name} value={value} />
    ),
    display: false,
  },
  {
    key: 'responding',
    label: (
      <TitleWithInfoIcon
        titleText="Connection Status"
        tooltipMsg={<div>Indicates if the host agent can connect to the management plane</div>}
      />
    ),
    // Don't want 'Responding' string to line wrap, use Text component
    CellComponent: RespondingCell,
  },
  {
    key: 'roleStatuses',
    label: (
      <TitleWithInfoIcon
        titleText="Role Status"
        tooltipMsg={<div>Shows the application status of all roles assigned to this host</div>}
      />
    ),
    CellComponent: RoleStatusesCell,
  },
  {
    key: 'schedulingStatus',
    label: 'Scheduling',
    render: (value, item) => {
      if (!item.hypervisor) {
        return 'N/A'
      }
      return (
        <div>
          {value}
          {item?.hypervisor?.service?.disabled_reason && (
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Badge
                variant="warning"
                text={
                  item?.hypervisor?.running_vms > 0
                    ? 'Entering Maintenance Mode'
                    : 'Maintenance Mode'
                }
                ellipsisAt={25}
                tooltipBody={
                  item?.hypervisor?.running_vms > 0
                    ? 'Migrations may be in-progress, or may need manual intervention to migrate remaining active VMs. Please check maintenance mode progress within host details.'
                    : null
                }
              />
            </div>
          )}
        </div>
      )
    },
  },
  {
    key: 'clusterName',
    label: 'Cluster',
  },
  {
    key: 'physicalNetworkLabels',
    label: 'Physical Network Label',
    width: 100,
    display: false,
    CellComponent: (props) => {
      const physicalNetworkLabels = props?.value || []
      return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
    },
  },
  {
    key: 'interfaces',
    label: 'IP Address',
    CellComponent: (props) => <IpAddressCellComponent value={props?.value} />,
  },
  {
    key: 'compute',
    label: 'Compute',
    CellComponent: ({ item }) => (
      <ResourceUsageIndicators
        usageValueConverter={{ fn: toMHz, units: 'MHz' }}
        usageStats={item?.usage?.compute}
        allocationStats={item?.usage?.computeAllocation}
      />
    ),
  },
  {
    key: 'memory',
    label: 'Memory',
    CellComponent: ({ item }) => (
      <ResourceUsageIndicators
        allocationValueConverter={{ fn: toGiB, units: 'GiB' }}
        usageStats={item?.usage?.memory}
        allocationStats={item?.usage?.memoryAllocation}
      />
    ),
  },
  {
    key: 'storage',
    label: 'Storage',
    CellComponent: ({ item }) => {
      return (
        <>
          <ResourceUsageIndicator
            units={item?.usage?.disk?.units}
            label="Root Disk Used"
            stats={item?.usage?.disk}
          />
          <ResourceUsageIndicator
            units={item?.usage?.ephemeralDisk?.units}
            label="Ephemeral Disk Used"
            stats={item?.usage?.ephemeralDisk}
          />
        </>
      )
    },
  },

  // {
  //   key: 'displayRoles',
  //   label: 'Assigned Roles',
  //   render: (values) => values.join(', '),
  //   display: isOpenCloud() ? false : true,
  // },
]

export default function HostsListPage({ hostlist = [], renderFromClustersPage = false }) {
  const { resMgr } = ApiClient.getInstance()
  const hasResmgr = resMgr.endpointPresent()
  const classes = useStyles({})
  const { history, match } = useReactRouter()

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const [selectedHosts, setSelectedHosts] = useState([])
  const [filteredHostsByCluster, setFilteredHostsByCluster] = useState([])
  const [showEditServerRolesModal, setShowEditServerRolesModal] = useState(false)
  const [showToggleMaintenanceModal, setShowToggleMaintenanceModal] = useState(false)
  const [showEnableMaintenanceModal, setShowEnableMaintenanceModal] = useState(false)
  const [showDisableMaintenanceModal, setShowDisableMaintenanceModal] = useState(false)
  const [showAuthorizeVmwareGatewayModal, setShowAuthorizeVmwareGatewayModal] = useState(false)
  const [showDeauthorizeHostDialog, setShowDeauthorizeHostDialog] = useState(false)

  const [hostConfigs, setHostConfigs] = useState([])
  const [blueprintAvailable, setBlueprintAvailable] = useState(false)

  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)

  const {
    loading: loadingHypervisors,
    reload: reloadHypervisors,
    refreshing: refreshingHypervisors,
  } = useListAction(listHypervisors, { params })
  const { reload: reloadHostNetworkConfigs } = useListAction(listHostNetworkConfigs)
  const { message, loading, reload, refreshing: refreshingHosts } = useListAction(listResmgrHosts, {
    params,
  })

  // Get host aggregate options
  const hostAggregateOptions = useSelector(hostAggregateOptionsSelector)

  const hostData = useSelector(resmgrHostsSelector)

  // Filter hosts based on hostList from cluster host group
  const hosts = renderFromClustersPage
    ? hostData.filter((host) => hostlist?.includes(host.id))
    : hostData

  const hypervisors = useSelector(hypervisorsSelector)
  const processedHosts = useMemo(() => {
    const hypervisorsByResmgrId = hypervisors.reduce((accum, hypervisor) => {
      return {
        ...accum,
        [hypervisor?.service?.host]: hypervisor,
      }
    }, {})
    return hosts.map((host) => {
      return {
        ...host,
        hypervisor: hypervisorsByResmgrId[host.id],
        name:
          features?.experimental?.pmov2_du_type === 'vmware' && !isOpenCloud()
            ? 'VMware Gateway'
            : host?.name,
      }
    })
  }, [hosts, hypervisors, features])

  const { reload: reloadStoragePools } = useListAction(listStoragePools, { params })
  const storagePools = useSelector(storagePoolsSelector)

  const { loading: loadingClusters } = useListAction(listClusterHostGroups)
  const clusters = useSelector(clusterHostGroupsSelector)

  // Get Cluster Blueprints
  const getClusterBlueprints = async () => {
    const blueprints = (await getBlueprints()) as Array<any>
    if (blueprints?.length) {
      setBlueprintAvailable(true)
      return
    }

    setBlueprintAvailable(false)
  }

  const searchFunctions = useMemo(() => getSearchFunctions(isVmware), [isVmware])

  useEffect(() => {
    getClusterBlueprints()
  }, [])

  useEffect(() => {
    // Pull fresh data when visiting resource utilization tab
    if (match?.params?.tab === IHostsListPageTabs.ResourceUtilization) {
      reload(true, false)
      reloadStoragePools(true, false)
      reloadHypervisors(true, false)
    }
  }, [match.params.tab])

  useEffect(() => {
    const getHostConfigs = async () => {
      const configs: any = await getHostNetworkConfigs()
      setHostConfigs(configs)
    }
    getHostConfigs()
  }, [])

  // Todo: Need to query host roles and see if they are equal to the blueprint values, check with backend on plausibility of this

  const vmwareBatchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (hosts) =>
        hosts.length === 1 &&
        hosts[0]?.extensions?.hypervisor_details?.data?.hypervisor_type === 'VMWareCluster',
      disabledTooltip:
        'VMware Gateway configuration can only be managed for individual hypervisors, not for VMware Cluster hypervisors.',
      label: 'Manage VMware Gateway Configuration',
      icon: isOpenCloud() ? 'pen-to-square' : 'person-to-portal',
      handleAction: (selected) => {
        setSelectedHosts(selected)
        setShowAuthorizeVmwareGatewayModal(true)
      },
      refreshAfterSuccess: false,
    },
  ]

  const defaultBatchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (hosts) => {
        return hosts.length > 0 && hostConfigs.length > 0
      },
      disabledTooltip:
        hostConfigs.length === 0
          ? 'You must first create host configurations in the cluster blueprint before assigning any cluster roles'
          : '',
      label: 'Edit Roles',
      icon: 'pen-to-square',
      handleAction: (selected) => {
        setSelectedHosts(selected)
        setShowEditServerRolesModal(true)
      },
      refreshAfterSuccess: false,
    },
  ]

  const batchActions = isVmware ? vmwareBatchActions : defaultBatchActions

  const pcdDropdownBatchActions: GridDropdownBatchActionGroup<SelectorModel>[] = useMemo(
    () => [
      {
        label: 'Other Actions',
        actions: [
          {
            cond: (hosts) => {
              return (
                hosts.length > 0 &&
                hosts.every(
                  (host) => host?.hypervisor && !host?.hypervisor?.service?.disabled_reason,
                )
              )
            },
            label: 'Enable/Disable Scheduling',
            icon: 'calendar-xmark',
            tooltip:
              'Scheduling toggle is only applicable to hypervisors that are not in maintenance mode',
            BatchActionButton: getGridDialogButton(ToggleMaintenanceModeDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            cond: (hosts) => {
              return (
                hosts.length === 1 &&
                !!hosts?.[0]?.hypervisor &&
                hosts?.[0]?.schedulingStatus === 'Enabled'
              )
            },
            label: 'Enable Maintenance Mode',
            icon: 'screwdriver-wrench',
            tooltip: 'Maintenance Mode is only applicable to active hypervisors',
            BatchActionButton: getGridDialogButton(EnableMaintenanceModeDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            cond: (hosts) => {
              return hosts.length === 1 && hosts?.[0]?.hypervisor?.service?.disabled_reason
            },
            label: 'Disable Maintenance Mode',
            icon: 'ban',
            BatchActionButton: getGridDialogButton(DisableMaintenanceDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            label: 'Update Allocation Ratios',
            icon: 'chart-pie',
            BatchActionButton: getGridDialogButton(AllocationRatioModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            cond: (hosts) => {
              return hosts.length === 1
            },
            label: 'Remove All Roles',
            icon: 'square-minus',
            disabledTooltip: 'You can only update one host at a time',
            BatchActionButton: getGridDialogButton(DeauthorizeRolesDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          // {
          //   cond: (hosts) => {
          //     return hosts.every((host) => host.roles?.length === 0)
          //   },
          //   label: 'Decommission',
          //   icon: 'trash-alt',
          //   tooltip:
          //     'You may only decommission hosts that have no roles attached. To prepare a host for decommission, you may invoke the "Remove All Roles" action on it',
          //   BatchActionButton: getGridDialogButton(DeauthorizeHostDialog, null, {
          //     className: classes.dropdownAction,
          //   }),
          // },
          // TODO: Add Assign Clusters for next release
          // {
          //   cond: (hosts) => {
          //     return hosts.length > 0 && hosts.every((host) => host?.hypervisor)
          //   },
          //   label: 'Assign Clusters',
          //   icon: 'plus-square',
          //   tooltip: 'Clusters can only be assigned to hypervisors',
          //   BatchActionButton: getGridDialogButton(AssignClusterDialog, null, {
          //     className: classes.dropdownAction,
          //   }),
          // },
        ],
      },
    ],
    [classes],
  )

  const vmwareDropdownBatchActions: GridDropdownBatchActionGroup<SelectorModel>[] = useMemo(
    () => [
      {
        label: 'Other Actions',
        actions: [
          {
            label: 'Update Allocation Ratios',
            icon: 'chart-pie',
            BatchActionButton: getGridDialogButton(AllocationRatioModal, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            cond: (hosts) => {
              return hosts.length === 1
            },
            label: 'Remove All Roles',
            icon: 'square-minus',
            BatchActionButton: getGridDialogButton(DeauthorizeRolesDialog, null, {
              className: classes.dropdownAction,
            }),
          },
          {
            cond: (hosts) => {
              return hosts.length === 1 && hosts[0]?.v1Roles?.length === 0
            },
            label: 'Deauthorize',
            icon: 'ban',
            tooltip: 'All roles must be removed before deauthorizing',
            BatchActionButton: getGridDialogButton(DeauthorizeHostModal, null, {
              className: classes.dropdownAction,
            }),
          },
        ],
      },
    ],
    [classes],
  )

  const openCloudDropdownBatchActions: GridDropdownBatchActionGroup<SelectorModel>[] = useMemo(
    () => (isVmware ? vmwareDropdownBatchActions : pcdDropdownBatchActions),
    [classes, isVmware],
  )

  const dropdownBatchActions = isOpenCloud()
    ? openCloudDropdownBatchActions
    : pcdDropdownBatchActions

  // Cluster filter options
  const clusterOptions = useMemo(() => {
    return [
      ...[
        {
          label: `-- ${UNASSIGNED_CLUSTER} --`,
          value: UNASSIGNED_CLUSTER,
        },
      ],
      ...(clusters?.map((cluster: IClusterHostGroup) => ({
        label: cluster.name,
        value: cluster.name,
      })) || []),
    ]
  }, [clusters])

  const clusterHostsByName = useMemo(() => {
    return clusters.reduce((accum, cluster) => {
      return {
        ...accum,
        [cluster.name]: cluster.hostlist,
      }
    }, {})
  }, [clusters])

  const handleApplyClusterFilter = useCallback(
    (selectedItems: Item[]) => {
      if (!selectedItems?.length) {
        setFilteredHostsByCluster([])
        return
      }
      setFilteredHostsByCluster(
        processedHosts.filter((host) => {
          const clusterSelected = flatten(
            selectedItems?.map((item) => clusterHostsByName[item.value] || []),
          ).includes(host?.id)
          if (selectedItems.some((item) => item.value === UNASSIGNED_CLUSTER)) {
            // return true for all hosts that are not
            // part of any cluster
            const allHostsInClusters = flatten(clusters.map((cluster) => cluster.hostlist || []))
            return clusterSelected || !allHostsInClusters.includes(host.id)
          }
          return clusterSelected
        }),
      )

      // Send async event to update graphs when cluster filter is applied
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('refreshGraph'))
      }, 0)
    },
    [processedHosts, clusters, clusterHostsByName],
  )

  const AuthorizeVmwareGatewayModalComponent = isOpenCloud()
    ? AuthorizeVmwareGatewayModalCopy
    : AuthorizeVmwareGatewayModal

  // Filter Option - Filter by Host Aggregate
  const dropdownFilters = useMemo(
    () => [
      {
        key: 'hostAggregate',
        label: 'By Host Aggregate',
        FilterComponent: StringMultiDropdownFilter,
        filterComponentProps: {
          label: 'By Host Aggregates',
        },
        filterComponentOptionsPropName: 'dropdownOptions',
        getOptionsFn: () => {
          return hostAggregateOptions
        },
        equalityComparerFn: (item, values) => {
          return item?.hostAggregates?.some((hostAggregate) => values?.includes(hostAggregate))
        },
      },
    ],
    [hostAggregateOptions],
  )

  const dataName = isVmware && isOpenCloud() ? 'VMware Gateway Appliances' : 'Cluster Hosts'

  return (
    <>
      {!renderFromClustersPage && (
        <>
          <DocumentMeta title={dataName} breadcrumbs />
          <HeaderRefreshPortal>
            <RadioDropdownFilter
              width={300}
              label="Cluster"
              items={clusterOptions}
              loading={loadingClusters}
              data={processedHosts}
              handleApplyFilter={handleApplyClusterFilter}
            />
          </HeaderRefreshPortal>
          <HeaderPrimaryActionPortal>
            {!isReadonlyUser() && (
              <CreateButton
                onClick={() =>
                  history.push(
                    isVmware
                      ? routeManager.routes.openstack.addVmwareGateway.path()
                      : routeManager.routes.openstack.addHosts.path(),
                  )
                }
              >
                {isVmware ? 'Add VMware Gateway' : 'Add New Hosts'}
              </CreateButton>
            )}
          </HeaderPrimaryActionPortal>
          <PollingData
            hidden
            loading={loading}
            onReload={() => {
              reload(true, false)
              reloadStoragePools(true, false)
              return reloadHypervisors(true, false)
            }}
            refreshDuration={1000 * 60 * 3}
          />
        </>
      )}
      {hasResmgr ? (
        <>
          {showEditServerRolesModal && (
            <EditServerRolesModal
              onClose={() => setShowEditServerRolesModal(false)}
              rows={selectedHosts}
              allHostsDetails={hosts}
            />
          )}
          {showToggleMaintenanceModal && (
            <ToggleMaintenanceModeDialog
              onClose={() => setShowToggleMaintenanceModal(false)}
              rows={selectedHosts}
            />
          )}
          {showEnableMaintenanceModal && (
            <EnableMaintenanceModeDialog
              onClose={() => setShowEnableMaintenanceModal(false)}
              rows={selectedHosts}
            />
          )}
          {showDisableMaintenanceModal && (
            <DisableMaintenanceDialog
              onClose={() => setShowDisableMaintenanceModal(false)}
              rows={selectedHosts}
            />
          )}
          {showAuthorizeVmwareGatewayModal && (
            <AuthorizeVmwareGatewayModalComponent
              onClose={() => setShowAuthorizeVmwareGatewayModal(false)}
              rows={selectedHosts}
            />
          )}
          {showDeauthorizeHostDialog && (
            <DeauthorizeHostDialog
              onClose={() => setShowDeauthorizeHostDialog(false)}
              rows={selectedHosts}
            />
          )}

          {renderFromClustersPage ? (
            <ListContainer<ModelDataKey, SelectorModel>
              dataKey={DataKeys.ResmgrHosts}
              label={dataName}
              searchTargets={searchTargets}
              searchFunctions={searchFunctions}
              uniqueIdentifier="id"
              loading={loading}
              loadingMessage={message}
              onRefresh={() => {
                reload(true)
                reloadHypervisors(true)
                reloadHostNetworkConfigs(true)
              }}
              data={processedHosts}
              dropdownBatchActions={dropdownBatchActions}
              columns={columns}
              getParamsUpdater={getParamsUpdater}
              batchActions={batchActions}
              multiSelection
              disableAddButton={isVmware && isOpenCloud() ? false : !blueprintAvailable}
              disabledAddButtonInfo="Cluster blueprint not available"
              {...pick(listTablePrefs, params)}
              dropdownFilters={dropdownFilters}
              showBreadcrumbs={false}
              hideHeaderPortals={true}
            />
          ) : (
            <Tabs route={routeManager.routes.openstack.hosts} routeKey="hoststab">
              <Tab
                label={isOpenCloud() && isVmware ? 'VMware Gateway Appliances' : 'Hosts'}
                value={IHostsListPageTabs.HostsList}
              >
                <>
                  {!isVmware && (
                    <AddServersPage addRoute={routeManager.routes.openstack.addHosts} />
                  )}
                  <ListContainer<ModelDataKey, SelectorModel>
                    dataKey={DataKeys.ResmgrHosts}
                    label={dataName}
                    searchTargets={searchTargets}
                    searchFunctions={searchFunctions}
                    uniqueIdentifier="id"
                    loading={loading}
                    loadingMessage={message}
                    onRefresh={() => {
                      reload(true)
                      reloadHypervisors(true)
                      reloadStoragePools(true)
                      reloadHostNetworkConfigs(true)
                    }}
                    data={filteredHostsByCluster}
                    dropdownBatchActions={dropdownBatchActions}
                    columns={columns}
                    getParamsUpdater={getParamsUpdater}
                    batchActions={batchActions}
                    multiSelection
                    disableAddButton={isVmware && isOpenCloud() ? false : !blueprintAvailable}
                    disabledAddButtonInfo="Cluster blueprint not available"
                    {...pick(listTablePrefs, params)}
                    dropdownFilters={dropdownFilters}
                    showBreadcrumbs={false}
                  />
                </>
              </Tab>
              <Tab
                label={`Resource Utilization (All ${
                  isVmware && isOpenCloud() ? 'VMware Gateway Appliances' : 'Hosts'
                })`}
                value={IHostsListPageTabs.ResourceUtilization}
              >
                <HostsListPageHeader
                  hosts={filteredHostsByCluster}
                  hypervisors={hypervisors}
                  storagePools={storagePools}
                  showAllocation
                  showOnlyForSelectedHosts={
                    !!hosts?.length && filteredHostsByCluster?.length !== hosts?.length
                  }
                />
              </Tab>
            </Tabs>
          )}
        </>
      ) : (
        <div className={classes.noResmgr}>
          <Text variant="subtitle1">
            {`Host management is only available with ${getProductName()}`}
          </Text>
        </div>
      )}
    </>
  )
}

const getIconOrBubbleColor = (status, theme: Theme) =>
  ({
    ok: theme.palette.green.main,
    failed: theme.palette.red.main,
  }[status] || theme.palette.grey[500])

const useStyles = makeStyles<Theme, { state?: string; responding?: boolean }>((theme) => ({
  noResmgr: {
    padding: '40px',
    textAlign: 'center',
    background: 'white',
  },
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'start',
    },
  },
  marginRight: {
    marginRight: 8,
  },
  circle: {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap',
    '&:before': {
      content: '""',
      display: 'inherit',
      height: 12,
      width: 12,
      borderRadius: '50%',
      backgroundColor: ({ state, responding }) =>
        responding ? getIconOrBubbleColor(state, theme) : theme.palette.red.main,
    },
  },
  hostStatusCell: {
    display: 'grid',
    gap: 8,
  },
  missingPackages: {
    color: theme.palette.yellow.main,
    marginRight: 6,
  },
  warning: {
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: 12,
    marginRight: 4,
    color: ({ state, responding }) =>
      responding ? getIconOrBubbleColor(state, theme) : theme.palette.red.main,
  },
  dot: {
    fontSize: 12,
    marginRight: 4,
  },
  green: {
    color: theme.palette.green.main,
  },
  yellow: {
    color: theme.palette.yellow.main,
  },
  red: {
    color: theme.palette.red.main,
  },
  orange: {
    color: theme.palette.orange.main,
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  ipAddressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    gap: theme.spacing(1),
  },
}))
